/**
 * HeatmapWithComparison Component
 *
 * Enhanced heatmap with integrated Lens comparison panel.
 * Preserves existing matrix layout but adds:
 * - Clickable model headers for selection (max 2)
 * - Clickable criterion rows to set active criterion
 * - Lens panel below matrix showing side-by-side comparison
 *
 * Part of Consensus Heatmap Comparison feature (behind newDesign flag)
 */

import React, { useEffect, useState } from 'react';
import { getModelDisplayInfo } from '../../../utils/modelDisplayName';
import { getModelLogo } from '../../../utils/logoMapping';
import type { ConsensusData, Criterion, PerModelState, PairwiseVerdict } from '../types';
import { CRITERION_METADATA } from '../types';
import type { ModelResponse } from '../../../services/comparison';
import { useComparisonState } from '../hooks/useComparisonState';
import { LensComparisonPanel } from './LensComparisonPanel';
import { ResponseDetailView } from './ResponseDetailView';
import { ClaimGroupsDisplay } from './ClaimGroupsDisplay';
import { useConsensusAnalysis } from '../hooks/useConsensusAnalysis';
import { useConsensusAnalysisContext } from '../context/ConsensusAnalysisContext';
import { logger } from '../../../lib/logging';
import { useSearchParams } from 'react-router-dom';
import { ConsensusGuideModal } from '../../../components/results/consensus/ConsensusGuideModal';

export interface HeatmapWithComparisonProps {
  data: ConsensusData;
  responses: ModelResponse[];
  correlationId?: string;
  hiddenModelKeys?: Set<string>;
  showScores?: boolean;
  confidenceData?: Record<string, Record<string, {
    averageScore: number;
    level?: 'high' | 'medium' | 'low';
    judgeAgreement?: string;
  }>>;
}

const STATE_COLORS: Record<PerModelState, { bg: string; text: string; symbol: string }> = {
  agree: { bg: 'bg-green-500/20', text: 'text-green-400', symbol: '✓' },
  mixed: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', symbol: '≈' },
  differ: { bg: 'bg-red-500/20', text: 'text-red-400', symbol: '✕' }
};

const VERDICT_COLORS: Record<PairwiseVerdict, { bg: string; text: string; border: string }> = {
  agree: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/50' },
  partial: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/50' },
  disagree: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/50' }
};

export const HeatmapWithComparison: React.FC<HeatmapWithComparisonProps> = ({
  data,
  responses,
  correlationId,
  hiddenModelKeys = new Set(),
  showScores = true,
  confidenceData
}) => {
  const sharedContext = useConsensusAnalysisContext();
  const {
    selectedModels,
    activeCriterion,
    canCompare,
    toggleModel,
    setActiveCriterion,
    clearSelection
  } = useComparisonState();

  const [searchParams, setSearchParams] = useSearchParams();
  const [rdvOpen, setRdvOpen] = useState<'left' | 'right' | null>(null);
  const [savedScrollY, setSavedScrollY] = useState(0);
  const [showGuideModal, setShowGuideModal] = useState(false);

  // Check consensus analysis status
  const {
    status: analysisStatus,
    checkStatus,
    error: analysisError,
    isLoading: analysisLoading
  } = sharedContext || useConsensusAnalysis(data.comparisonId);
  const analysisComplete = analysisStatus?.status === 'completed';
  const analysisProcessing = analysisStatus?.status === 'processing' || analysisLoading;

  // Load consensus analysis status on mount - ensure it completes before rendering
  useEffect(() => {
    const initializeStatus = async () => {
      try {
        await checkStatus();
      } catch (err) {
        logger.error('Failed to check consensus analysis status on mount', {
          comparisonId: data.comparisonId,
          error: err
        });
      }
    };

    initializeStatus();
  }, [checkStatus, data.comparisonId]);

  // Check URL for rdv parameter
  useEffect(() => {
    const rdvParam = searchParams.get('rdv');
    if (rdvParam === 'left' || rdvParam === 'right') {
      setRdvOpen(rdvParam);
    }
  }, [searchParams]);

  // Filter out hidden models
  const visibleResponses = data.responses.filter(r => !hiddenModelKeys.has(r.modelKey));

  const handleCriterionClick = (criterion: Criterion) => {
    // Allow criterion clicks regardless of model selection
    // With 2 models: shows detailed lens comparison
    // With 0-1 models: shows criterion summary/analysis
    const isOpening = activeCriterion !== criterion;

    if (activeCriterion === criterion) {
      setActiveCriterion(null); // Close if already open
    } else {
      setActiveCriterion(criterion);
    }

    // Telemetry: Log criterion row click
    if (isOpening) {
      logger.info('consensus.criterion_row_clicked', undefined, {
        op: 'criterion_row_clicked',
        component: 'HeatmapWithComparison',
        correlationId: correlationId || crypto.randomUUID(),
        criterion,
        models: selectedModels,
        modelCount: selectedModels.length,
        success: true
      });
    }
  };

  // Get criterion summary data (shown when 0 or 1 models selected)
  const getCriterionSummary = () => {
    if (!activeCriterion) return null;

    const criterionAnalysis = data.criteria.find(c => c.criterion === activeCriterion);

    if (!criterionAnalysis) return null;

    return {
      criterion: activeCriterion,
      label: criterionAnalysis.label,
      description: criterionAnalysis.description,
      consensusScore: criterionAnalysis.consensusScore,
      summary: criterionAnalysis.summary,
      modelCount: visibleResponses.length,
      visibleResponses
    };
  };

  // Get lens data for active criterion (shown when exactly 2 models selected)
  const getLensData = () => {
    if (!activeCriterion || selectedModels.length !== 2) return null;

    const modelAKey = selectedModels[0];
    const modelBKey = selectedModels[1];

    const responseA = data.responses.find(r => r.modelKey === modelAKey);
    const responseB = data.responses.find(r => r.modelKey === modelBKey);

    if (!responseA || !responseB) return null;

    const stateA = responseA.criterionStates[activeCriterion];
    const stateB = responseB.criterionStates[activeCriterion];

    const scoreA = confidenceData?.[activeCriterion]?.[modelAKey]?.averageScore ?? 75;
    const scoreB = confidenceData?.[activeCriterion]?.[modelBKey]?.averageScore ?? 75;

    const criterionAnalysis = data.criteria.find(c => c.criterion === activeCriterion);

    return {
      responseA: responseA.response,
      responseB: responseB.response,
      stateA,
      stateB,
      scoreA,
      scoreB,
      judgeAnalysis: criterionAnalysis?.summary
    };
  };

  const criterionSummary = getCriterionSummary();
  const lensData = getLensData();

  // Telemetry: Log when comparison mode is enabled (2 models selected)
  useEffect(() => {
    if (canCompare) {
      logger.info('consensus.compare_models_selected', undefined, {
        op: 'compare_models_selected',
        component: 'HeatmapWithComparison',
        correlationId: correlationId || crypto.randomUUID(),
        models: selectedModels,
        modelCount: selectedModels.length,
        success: true
      });
    }
  }, [canCompare, selectedModels, correlationId]);

  // Telemetry: Log when lens panel is rendered
  useEffect(() => {
    if (lensData && activeCriterion) {
      logger.info('consensus.lens_rendered', undefined, {
        op: 'lens_rendered',
        component: 'HeatmapWithComparison',
        correlationId: correlationId || crypto.randomUUID(),
        criterion: activeCriterion,
        models: selectedModels,
        success: true
      });
    }
  }, [lensData, activeCriterion, selectedModels, correlationId]);

  // Handler: Open RDV modal
  const handleExpandResponse = (side: 'left' | 'right') => {
    // Save scroll position for restoration
    setSavedScrollY(window.scrollY);

    // Update URL with rdv parameter
    const newParams = new URLSearchParams(searchParams);
    newParams.set('rdv', side);
    setSearchParams(newParams, { replace: false }); // Push to history

    setRdvOpen(side);

    // Telemetry
    logger.info('consensus.open_rdv', undefined, {
      op: 'open_rdv',
      component: 'HeatmapWithComparison',
      correlationId: correlationId || crypto.randomUUID(),
      source: 'expander',
      side,
      criterion: activeCriterion,
      models: selectedModels,
      success: true
    });
  };

  // Handler: Close RDV modal
  const handleCloseRdv = () => {
    // Remove rdv parameter from URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('rdv');
    setSearchParams(newParams, { replace: false }); // Allow back button

    setRdvOpen(null);

    // Restore scroll position
    setTimeout(() => {
      window.scrollTo(0, savedScrollY);
    }, 0);

    // Telemetry: Log state restoration
    logger.info('consensus.state_restored', undefined, {
      op: 'state_restored',
      component: 'HeatmapWithComparison',
      correlationId: correlationId || crypto.randomUUID(),
      criterion: activeCriterion,
      models: selectedModels,
      scrollY: savedScrollY,
      success: true
    });
  };

  // Get RDV data
  const getRdvData = () => {
    if (!rdvOpen || selectedModels.length !== 2) return null;

    const modelKey = rdvOpen === 'left' ? selectedModels[0] : selectedModels[1];
    const response = data.responses.find(r => r.modelKey === modelKey);

    if (!response || !activeCriterion) return null;

    const state = response.criterionStates[activeCriterion];
    const score = confidenceData?.[activeCriterion]?.[modelKey]?.averageScore;

    return {
      modelKey,
      response: response.response,
      criterion: activeCriterion,
      criterionLabel: CRITERION_METADATA[activeCriterion].label,
      state,
      score
    };
  };

  const rdvData = getRdvData();

  return (
    <div className="space-y-4" data-testid="heatmap-with-comparison">
      {/* Default Criterion Summary Panel (when < 2 models selected) */}
      {criterionSummary && selectedModels.length < 2 && (
        <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">{CRITERION_METADATA[criterionSummary.criterion].icon}</span>
              <div>
                <h3 className="text-lg font-semibold text-white">{criterionSummary.label}</h3>
                <p className="text-sm text-slate-400">{criterionSummary.description}</p>
              </div>
            </div>
            <button
              onClick={() => setActiveCriterion(null)}
              className="text-slate-400 hover:text-white text-2xl leading-none"
              title="Close"
            >
              ✕
            </button>
          </div>

          <div className="mb-4 p-3 bg-slate-700/20 rounded-lg">
            <p className="text-sm text-slate-400 mb-2">Consensus Score</p>
            <div className="flex items-center gap-3">
              <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-cyan-500 to-blue-500"
                  style={{ width: `${Math.min(criterionSummary.consensusScore, 100)}%` }}
                />
              </div>
              <span className="text-lg font-semibold text-white">{Math.round(criterionSummary.consensusScore)}%</span>
            </div>
          </div>

          {criterionSummary.summary && (
            <div className="mb-4">
              <p className="text-sm text-slate-400 mb-2 font-medium">Analysis Summary</p>
              <p className="text-sm text-slate-300 leading-relaxed">{criterionSummary.summary}</p>
            </div>
          )}

          {selectedModels.length === 0 && (
            <div className="p-3 bg-slate-700/20 rounded-lg">
              <p className="text-xs text-slate-400 text-center">
                💡 Select two models above to see a detailed side-by-side comparison of their responses for this criterion.
              </p>
            </div>
          )}

          {selectedModels.length === 1 && (
            <div className="p-3 bg-slate-700/20 rounded-lg">
              <p className="text-xs text-slate-400 text-center">
                💡 Select one more model above to see a detailed side-by-side comparison.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Lens Comparison Panel (when exactly 2 models selected) */}
      {lensData && activeCriterion && (
        <LensComparisonPanel
          criterion={activeCriterion}
          modelAKey={selectedModels[0]}
          modelBKey={selectedModels[1]}
          responseA={lensData.responseA}
          responseB={lensData.responseB}
          stateA={lensData.stateA}
          stateB={lensData.stateB}
          scoreA={lensData.scoreA}
          scoreB={lensData.scoreB}
          judgeAnalysis={lensData.judgeAnalysis}
          onClose={() => setActiveCriterion(null)}
          onExpandResponse={handleExpandResponse}
          comparisonId={data.comparisonId}
        />
      )}

      {/* Consensus Analysis Section - Always visible */}
      <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 mt-4">
        {analysisProcessing && !analysisComplete && (
          <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center gap-2 text-sm text-blue-200">
            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Consensus analysis in progress…</span>
          </div>
        )}

        {analysisError && (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm font-medium mb-2">⚠️ Analysis Status Error</p>
            <p className="text-red-300/80 text-sm">{analysisError}</p>
            <button
              onClick={() => checkStatus()}
              className="mt-3 px-3 py-1 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded text-red-300 text-xs font-medium transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {analysisComplete ? (
          // Show actual claim groups when analysis is complete
          <>
            {/* 🔍 DIAGNOSTIC: Log before rendering ClaimGroupsDisplay */}
            {console.log('[🔍 DIAGNOSTIC] HeatmapWithComparison RENDERING ClaimGroupsDisplay', {
              comparisonId: data.comparisonId,
              responsesCount: responses.length,
              responsesPreview: responses.slice(0, 2).map(r => ({
                id: r.id,
                provider: r.provider?.name,
                model: r.model?.name
              })),
              analysisComplete,
              timestamp: new Date().toISOString()
            })}
            <ClaimGroupsDisplay
              comparisonId={data.comparisonId}
              responses={responses}
            />
          </>
        ) : (
          // Show empty state before analysis runs
          <div className="space-y-4">
            {/* Empty Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Universal Agreement Card */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">🟢</span>
                  <span className="text-slate-300 text-xs font-medium lg:text-sm">
                    <span className="hidden lg:inline">Universal Agreement</span>
                    <span className="lg:hidden">Universal</span>
                  </span>
                </div>
                <div className="text-2xl lg:text-3xl font-bold text-slate-400 mb-1">0</div>
                <div className="text-xs text-slate-500">
                  <span className="hidden lg:inline">All models agree</span>
                  <span className="lg:hidden">All agree</span>
                </div>
              </div>

              {/* Majority Agreement Card */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">🔵</span>
                  <span className="text-slate-300 text-xs font-medium lg:text-sm">
                    <span className="hidden lg:inline">Majority Agreement</span>
                    <span className="lg:hidden">Majority</span>
                  </span>
                </div>
                <div className="text-2xl lg:text-3xl font-bold text-slate-400 mb-1">0</div>
                <div className="text-xs text-slate-500">
                  <span className="hidden lg:inline">Most models agree</span>
                  <span className="lg:hidden">Most agree</span>
                </div>
              </div>

              {/* Disputed Card */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">🟡</span>
                  <span className="text-slate-300 text-xs font-medium lg:text-sm">Disputed</span>
                </div>
                <div className="text-2xl lg:text-3xl font-bold text-slate-400 mb-1">0</div>
                <div className="text-xs text-slate-500">
                  <span className="hidden lg:inline">Some models differ</span>
                  <span className="lg:hidden">Some differ</span>
                </div>
              </div>

              {/* Unique Insight Card */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">⚪</span>
                  <span className="text-slate-300 text-xs font-medium lg:text-sm">
                    <span className="hidden lg:inline">Unique Insight</span>
                    <span className="lg:hidden">Unique</span>
                  </span>
                </div>
                <div className="text-2xl lg:text-3xl font-bold text-slate-400 mb-1">0</div>
                <div className="text-xs text-slate-500">
                  <span className="hidden lg:inline">Only one model</span>
                  <span className="lg:hidden">One model</span>
                </div>
              </div>
            </div>

            {/* Empty State Message */}
            <div className="text-center py-8">
              <p className="text-slate-400 text-sm">No claim groups found.</p>
            </div>
          </div>
        )}
      </div>

      {/* Response Detail View (RDV) Modal */}
      {rdvOpen && rdvData && (
        <ResponseDetailView
          modelKey={rdvData.modelKey}
          response={rdvData.response}
          criterion={rdvData.criterion}
          criterionLabel={rdvData.criterionLabel}
          state={rdvData.state}
          score={rdvData.score}
          onClose={handleCloseRdv}
        />
      )}

      {/* Consensus Guide Modal */}
      <ConsensusGuideModal
        isOpen={showGuideModal}
        onClose={() => setShowGuideModal(false)}
      />
    </div>
  );
};
